// 3D Viewer Component using Three.js
import * as THREE from 'three';
import { OrbitControls } from 'three/examples/jsm/controls/OrbitControls.js';
import { OBJLoader } from 'three/examples/jsm/loaders/OBJLoader.js';
import { STLLoader } from 'three/examples/jsm/loaders/STLLoader.js';
import { PLYLoader } from 'three/examples/jsm/loaders/PLYLoader.js';
import { GLTFLoader } from 'three/examples/jsm/loaders/GLTFLoader.js';

/**
 * 3D Viewer class for rendering OBJ, STL, PLY files
 */
export class Viewer3D {
    constructor(container) {
        this.container = typeof container === 'string'
            ? document.getElementById(container)
            : container;

        this.scene = null;
        this.camera = null;
        this.renderer = null;
        this.controls = null;
        this.currentModel = null;
        this.animationId = null;

        // Interaction
        this.raycaster = new THREE.Raycaster();
        this.mouse = new THREE.Vector2();
        this.tooltip = null;
        this.hoveredObject = null;

        this.init();
    }

    /**
     * Initialize the 3D scene
     */
    init() {
        const width = this.container.clientWidth;
        const height = this.container.clientHeight;

        // Scene
        this.scene = new THREE.Scene();
        this.scene.background = new THREE.Color(0x151518);

        // Camera
        this.camera = new THREE.PerspectiveCamera(60, width / height, 0.1, 1000);
        this.camera.position.set(0, 0, 5);

        // Renderer
        this.renderer = new THREE.WebGLRenderer({ antialias: true });
        this.renderer.setSize(width, height);
        this.renderer.setPixelRatio(Math.min(window.devicePixelRatio, 2));
        this.container.appendChild(this.renderer.domElement);

        // Controls
        this.controls = new OrbitControls(this.camera, this.renderer.domElement);
        this.controls.enableDamping = true;
        this.controls.dampingFactor = 0.05;
        this.controls.autoRotate = false;
        this.controls.autoRotateSpeed = 1.0;

        // Lighting
        this.setupLights();

        // Grid helper (subtle)
        const gridHelper = new THREE.GridHelper(10, 20, 0x333338, 0x222228);
        gridHelper.position.y = -1.5;
        this.scene.add(gridHelper);

        // Tooltip element
        this.tooltip = document.createElement('div');
        this.tooltip.className = 'viewer-tooltip';
        this.tooltip.style.position = 'absolute';
        this.tooltip.style.backgroundColor = 'rgba(0,0,0,0.8)';
        this.tooltip.style.color = '#fff';
        this.tooltip.style.padding = '5px 10px';
        this.tooltip.style.borderRadius = '4px';
        this.tooltip.style.pointerEvents = 'none';
        this.tooltip.style.display = 'none';
        this.tooltip.style.zIndex = '1000';
        this.tooltip.style.fontSize = '12px';
        document.body.appendChild(this.tooltip);

        // Handle resize
        window.addEventListener('resize', () => this.onResize());

        // Mouse move
        this.container.addEventListener('mousemove', (e) => this.onMouseMove(e));


        // Start animation loop
        this.animate();
    }

    /**
     * Set up scene lighting
     */
    setupLights() {
        // Ambient light
        const ambientLight = new THREE.AmbientLight(0xffffff, 0.4);
        this.scene.add(ambientLight);

        // Main directional light
        const mainLight = new THREE.DirectionalLight(0xffffff, 0.8);
        mainLight.position.set(5, 10, 7);
        this.scene.add(mainLight);

        // Fill light
        const fillLight = new THREE.DirectionalLight(0xd4a853, 0.3);
        fillLight.position.set(-5, 0, -5);
        this.scene.add(fillLight);

        // Rim light
        const rimLight = new THREE.DirectionalLight(0x7ec8e3, 0.2);
        rimLight.position.set(0, -5, -5);
        this.scene.add(rimLight);
    }

    /**
     * Load a 3D model from URL
     * @param {string} url - Model URL
     * @param {string} format - File format (obj, stl, ply)
     * @returns {Promise<THREE.Object3D>}
     */
    async loadModel(url, format, clearScene = true) {
        // Clear existing model if requested
        if (clearScene && this.currentModel) {
            this.clearModels();
        }

        const object = await this._loadRaw(url, format);

        // Center and scale the model
        this.centerAndScale(object);

        this.scene.add(object);
        this.currentModel = object;

        return object;
    }

    /**
     * Internal raw loader (no scene modification)
     */
    async _loadRaw(url, format) {
        const loaders = {
            obj: new OBJLoader(),
            stl: new STLLoader(),
            ply: new PLYLoader(),
            gltf: new GLTFLoader(),
            glb: new GLTFLoader()
        };

        const loader = loaders[format.toLowerCase()];
        if (!loader) {
            throw new Error(`Unsupported format: ${format}`);
        }

        return new Promise((resolve, reject) => {
            loader.load(
                url,
                (result) => {
                    let object;
                    const fmt = format.toLowerCase();
                    if (fmt === 'obj') {
                        object = result;
                    } else if (fmt === 'gltf' || fmt === 'glb') {
                        // GLTF/GLB returns a structured object with scene property
                        object = result.scene;
                    } else {
                        // STL, PLY return geometry
                        const geometry = result;
                        geometry.computeVertexNormals();
                        const material = new THREE.MeshStandardMaterial({
                            color: 0xd4a853,
                            metalness: 0.2,
                            roughness: 0.7,
                            flatShading: false
                        });
                        object = new THREE.Mesh(geometry, material);
                    }
                    resolve(object);
                },
                undefined,
                (error) => reject(error)
            );
        });
    }

    /**
     * Load multiple fragments as a composite group (preserves relative positions)
     * @param {Array} items - Array of {url, format, artifactId, name}
     */
    async loadCompositeModel(items) {
        this.clearModels();

        const group = new THREE.Group();
        group.name = "AssemblyGroup";

        const promises = items.map(async (item) => {
            try {
                const object = await this._loadRaw(item.url, item.format || 'obj');

                // Assign metadata to meshes for raycasting
                object.traverse((child) => {
                    if (child.isMesh) {
                        child.userData.artifactId = item.artifactId;
                        child.userData.name = item.name;
                        child.material.side = THREE.DoubleSide; // Ensure visibility

                        // Ensure we use a unique material for highlighting later
                        if (child.material) {
                            child.material = child.material.clone();
                        }
                    }
                });

                group.add(object);
            } catch (err) {
                console.error("Failed to load fragment", item, err);
            }
        });

        await Promise.all(promises);

        // Center the entire group
        this.centerAndScale(group);

        this.scene.add(group);
        this.currentModel = group;

        return group;
    }

    /**
     * Center and scale model to fit view
     * @param {THREE.Object3D} object 
     */
    centerAndScale(object) {
        // Recalculate bounding box
        const box = new THREE.Box3().setFromObject(object);

        // Calculate center of the bounding box
        const center = box.getCenter(new THREE.Vector3());

        // Subtract center from position to move it to (0,0,0)
        object.position.x += (object.position.x - center.x);
        object.position.y += (object.position.y - center.y);
        object.position.z += (object.position.z - center.z);

        // Subtract center from position to move it to (0,0,0)
        object.position.x += (object.position.x - center.x);
        object.position.y += (object.position.y - center.y);
        object.position.z += (object.position.z - center.z);

        // Removed destructive individual mesh centering loop that was corrupting assemblies


        // Re-scale to fit
        const newBox = new THREE.Box3().setFromObject(object);
        const size = newBox.getSize(new THREE.Vector3());
        const maxDim = Math.max(size.x, size.y, size.z);
        const scale = 3 / maxDim; // Fit within 3 units
        object.scale.setScalar(scale);

        // Reset position just in case
        object.position.set(0, 0, 0);
    }

    /**
     * Add a model to the scene (for combining fragments)
     * @param {THREE.Object3D} object 
     * @param {THREE.Vector3} position 
     */
    addModel(object, position = new THREE.Vector3()) {
        object.position.copy(position);
        this.scene.add(object);
    }

    /**
     * Clear all models from scene
     */
    clearModels() {
        const toRemove = [];
        this.scene.traverse((child) => {
            if ((child.isMesh || child.isGroup) && child !== this.scene) {
                // Don't remove lights or helpers if they are direct children of scene
                // (Our lights are added in setupLights, likely direct children)
                // Filter by name or type if needed, but safe approach:
                // If it looks like a model/group we loaded, remove it.
                if (child.name !== '' || child.isMesh || child.name === 'AssemblyGroup') {
                    toRemove.push(child);
                }
            }
        });

        // Also explicitly remove currentModel if tracked
        if (this.currentModel && !toRemove.includes(this.currentModel)) {
            toRemove.push(this.currentModel);
        }

        toRemove.forEach((obj) => {
            this.scene.remove(obj);
            // Dispose geometry/material if possible to free memory
            if (obj.geometry) obj.geometry.dispose();
            if (obj.material) {
                if (Array.isArray(obj.material)) obj.material.forEach(m => m.dispose());
                else obj.material.dispose();
            }
        });

        this.currentModel = null;
        this.renderer.render(this.scene, this.camera); // Clear visual buffer
    }

    /**
     * Toggle auto-rotation
     * @param {boolean} enabled 
     */
    setAutoRotate(enabled) {
        this.controls.autoRotate = enabled;
    }

    /**
     * Reset camera to default position
     */
    /**
     * Reset camera to default position
     */
    resetCamera() {
        this.setCameraPosition('iso');
        this.controls.reset();
    }

    /**
     * Set camera position for specific views
     * @param {string} view - 'iso', 'front', 'top', 'back', 'right'
     */
    /**
     * Set camera position for specific views, optionally fitting to an object
     * @param {string} view - 'iso', 'front', 'top', 'back', 'right'
     * @param {THREE.Object3D} [fitTarget] - Object to frame
     */
    setCameraPosition(view, fitTarget = null) {
        let distance = 8; // Default fallback

        // Calculate optimal distance if target provided
        if (fitTarget) {
            const box = new THREE.Box3().setFromObject(fitTarget);
            const size = box.getSize(new THREE.Vector3());
            const center = box.getCenter(new THREE.Vector3());

            // Use bounding sphere radius for more reliable fitting
            const maxDim = Math.max(size.x, size.y, size.z);
            const boundingSphereRadius = size.length() / 2; // Diagonal distance

            const fov = this.camera.fov * (Math.PI / 180);
            const aspect = this.camera.aspect || 1;

            // Calculate distance needed to fit the bounding sphere
            // Using both vertical and horizontal FOV to ensure fit in both dimensions
            const vFovFactor = boundingSphereRadius / Math.sin(fov / 2);
            const hFovFactor = boundingSphereRadius / Math.sin(Math.atan(Math.tan(fov / 2) * aspect));

            // Use the larger distance to ensure fit in both dimensions
            // Add 30% safety margin to prevent clipping
            let fitDist = Math.max(vFovFactor, hFovFactor) * 1.3;

            // Ensure minimum distance to prevent extreme close-ups
            distance = Math.max(fitDist, 3.5);
        }

        switch (view) {
            case 'front':
                this.camera.position.set(0, 0, distance);
                break;
            case 'back':
                this.camera.position.set(0, 0, -distance);
                break;
            case 'top':
                this.camera.position.set(0, distance, 0);
                break;
            case 'right':
                this.camera.position.set(distance, 0, 0);
                break;
            case 'iso':
            default:
                this.camera.position.set(distance * 0.6, distance * 0.6, distance * 0.6); // vector length ~ dist
                break;
        }
        this.camera.lookAt(new THREE.Vector3(0, 0, 0));
        this.controls.update();
        // Render immediately
        this.renderer.render(this.scene, this.camera);
    }

    /**
     * Set color of a model
     * @param {THREE.Object3D} model 
     * @param {string|number} color 
     */
    setModelColor(model, color) {
        if (!model) return;

        model.traverse((child) => {
            if (child.isMesh) {
                // Clone material to avoid affecting other objects sharing same material
                child.material = child.material.clone();
                child.material.color.set(color);
            }
        });

        // Render immediately
        this.renderer.render(this.scene, this.camera);
    }

    /**
     * Handle window resize
     */
    onResize() {
        const width = this.container.clientWidth;
        const height = this.container.clientHeight;

        this.camera.aspect = width / height;
        this.camera.updateProjectionMatrix();
        this.renderer.setSize(width, height);
    }

    /**
     * Animation loop
     */
    animate() {
        this.animationId = requestAnimationFrame(() => this.animate());
        this.controls.update();

        // Raycasting
        this.raycaster.setFromCamera(this.mouse, this.camera);

        // Find intersections - check ALL descendants recursively
        const intersects = this.raycaster.intersectObjects(this.scene.children, true);

        let found = false;

        if (intersects.length > 0) {
            // Filter out helpers and lines
            const validHit = intersects.find(hit => hit.object.type === 'Mesh' && hit.object.visible);

            if (validHit) {
                const object = validHit.object;

                // Traverse up to find the object that holds the userData (might be a group or the mesh itself)
                // In our case, ReconstructView will assign userData to the Mesh directly.
                const artifactId = object.userData.artifactId;
                const name = object.userData.name || object.name;

                if (artifactId || name) {
                    found = true;
                    this.tooltip.style.display = 'block';
                    this.tooltip.textContent = artifactId ? `Artifact #${artifactId}` : name;

                    // Highlight logic could go here (e.g. emissive color)
                    document.body.style.cursor = 'pointer';
                }
            }
        }

        if (!found) {
            this.tooltip.style.display = 'none';
            document.body.style.cursor = 'default';
        }

        this.renderer.render(this.scene, this.camera);
    }

    onMouseMove(event) {
        // Calculate mouse position in normalized device coordinates
        // (-1 to +1) for both components
        const rect = this.container.getBoundingClientRect();
        this.mouse.x = ((event.clientX - rect.left) / rect.width) * 2 - 1;
        this.mouse.y = -((event.clientY - rect.top) / rect.height) * 2 + 1;

        // Update tooltip position
        this.tooltip.style.left = (event.clientX + 10) + 'px';
        this.tooltip.style.top = (event.clientY + 10) + 'px';
    }

    /**
     * Clean up resources
     */
    dispose() {
        if (this.animationId) {
            cancelAnimationFrame(this.animationId);
        }

        this.controls.dispose();
        this.renderer.dispose();

        if (this.container && this.renderer.domElement) {
            this.container.removeChild(this.renderer.domElement);
        }

        if (this.tooltip && this.tooltip.parentNode) {
            this.tooltip.parentNode.removeChild(this.tooltip);
        }
    }
    /**
     * Capture screenshot of the current view
     * @param {number} width (optional)
     * @param {number} height (optional)
     * @param {string} type - mime type
     * @returns {string} data URL
     */
    captureScreenshot(width, height, type = 'image/png') {
        // If specific dimensions needed, we can resize, render, and restore
        const originalSize = new THREE.Vector2();
        this.renderer.getSize(originalSize);

        if (width && height) {
            this.renderer.setSize(width, height);
            this.camera.aspect = width / height;
            this.camera.updateProjectionMatrix();
        }

        this.renderer.render(this.scene, this.camera);
        const dataUrl = this.renderer.domElement.toDataURL(type);

        // Restore
        if (width && height) {
            this.renderer.setSize(originalSize.x, originalSize.y);
            this.camera.aspect = originalSize.x / originalSize.y;
            this.camera.updateProjectionMatrix();
            // Re-render immediately to prevent flicker
            this.renderer.render(this.scene, this.camera);
        }

        return dataUrl;
    }
}

/**
 * Create a simple 3D preview thumbnail (static render)
 * @param {string} url - Model URL
 * @param {string} format - File format
 * @param {number} size - Canvas size
 * @returns {Promise<string>} Data URL of the preview
 */
export async function create3DPreview(url, format, size = 200) {
    // Create temporary container
    const container = document.createElement('div');
    container.style.width = size + 'px';
    container.style.height = size + 'px';
    container.style.position = 'absolute';
    container.style.left = '-9999px';
    document.body.appendChild(container);

    try {
        const viewer = new Viewer3D(container);
        await viewer.loadModel(url, format);

        // Render one frame
        viewer.renderer.render(viewer.scene, viewer.camera);

        // Get image data
        const dataUrl = viewer.renderer.domElement.toDataURL('image/png');

        // Clean up
        viewer.dispose();
        document.body.removeChild(container);

        return dataUrl;
    } catch (error) {
        document.body.removeChild(container);
        throw error;
    }
}
